/**
 * EmailOutboxCard — every message the platform could not send while SMTP was
 * off sits in the outbox. Once credentials are pasted and email is switched on,
 * "Send queued emails" flushes the backlog.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Send, Trash2, Inbox } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

export const EmailOutboxCard = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['email-outbox'],
    queryFn: () => api.get('/email/outbox?limit=25').then((r) => r.data),
  });

  const flush = useMutation({
    mutationFn: () => api.post('/email/outbox/flush'),
    onSuccess: (res) => {
      const d = res.data || {};
      toast.success(`Sent ${d.sent} of ${d.processed}${d.failed ? ` · ${d.failed} failed` : ''}`, {
        description: d.errors?.length ? d.errors.join(' · ') : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['email-outbox'] });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not send the queue')),
  });

  const discard = useMutation({
    mutationFn: (id) => api.delete(`/email/outbox/${id}`),
    onSuccess: () => {
      toast.success('Discarded');
      queryClient.invalidateQueries({ queryKey: ['email-outbox'] });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not discard')),
  });

  const counts = data?.counts || {};
  const pending = (counts.queued || 0) + (counts.failed || 0);
  const items = (data?.items || []).filter((i) => i.status !== 'discarded');

  return (
    <div className="mt-8 p-4 bg-slate-50 rounded-lg space-y-3" data-testid="email-outbox-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-medium text-sm flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Email outbox
          </h4>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Invites, reminders, decision letters and receipts raised while email was off are
            held here — nothing is lost. Paste your mail credentials above, switch Email
            Sending on, save, then send the queue.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => flush.mutate()}
          disabled={flush.isPending || !pending}
          data-testid="flush-email-outbox-btn"
        >
          {flush.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Send queued emails
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="email-outbox-counts">
        <Badge variant="outline">Queued {counts.queued || 0}</Badge>
        <Badge variant="outline">Failed {counts.failed || 0}</Badge>
        <Badge variant="outline">Sent {counts.sent || 0}</Badge>
        {!data?.smtp_enabled && (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Email sending is off</Badge>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-slate-500">Loading the outbox…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="email-outbox-empty">
          Nothing waiting. Everything the platform raised has gone out.
        </p>
      ) : (
        <div className="divide-y border rounded-md bg-white" data-testid="email-outbox-list">
          {items.map((item) => (
            <div key={item.id || item._id} className="flex items-center gap-3 px-3 py-2 text-xs">
              <span
                className={`px-2 py-0.5 rounded-full font-medium ${
                  item.status === 'sent'
                    ? 'bg-green-100 text-green-700'
                    : item.status === 'failed'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {item.status}
              </span>
              <span className="flex-1 min-w-0">
                <span className="font-medium truncate block">{item.subject || '(no subject)'}</span>
                <span className="text-slate-500">{item.to_email}</span>
              </span>
              {item.last_error && <span className="text-red-500 truncate max-w-[14rem]">{item.last_error}</span>}
              {item.status !== 'sent' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => discard.mutate(item.id || item._id)}
                  data-testid={`discard-outbox-${item.id || item._id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailOutboxCard;
